import type {
  ColumnGroupPreferenceState,
  ColumnPreferenceState,
  IBlotterPreferenceState,
} from 'components/shared/Blotter/Grid/blotter.model';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import { limitOrderThunks } from 'state/secondaryMarket/limit-order/limitOrder.thunk';
import { liveThunks } from 'state/secondaryMarket/live/live.thunk';
import type { ProductChange, SecondaryMarketProduct } from 'state/secondaryMarket/productSearch';
import {
  secondaryMarketProductRefreshRequested,
  secondaryMarketProductSearchFocused,
  secondaryMarketProductsClearAll,
  secondaryMarketProductsColumnStateChanged,
  secondaryMarketProductsRemoveItem,
} from 'state/secondaryMarket/productSearch';
import { getIsSearchingProducts } from 'state/secondaryMarket/productSearch/productSearch.selectors';
import {
  searchProductUnderlyingsThunk,
  secondaryMarketAstroProductFetchRequestedThunk,
  secondaryMarketProductRefreshAllRequested,
  secondaryMarketProductsValueChangedThunk,
} from 'state/secondaryMarket/productSearch/productSearch.thunks';
import { secondaryMarketQuoteByIsinReloadRequested } from 'state/secondaryMarket/realtimeQuote/quote';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import { fromSecondaryMarket } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { isSecondaryMarketCpOrderEnabledForUser, isSecondaryMarketLiveQuoteEnabledForUser } from 'state/user';
import { secondaryCPOrderTabCreatedFromProductSearchThunk } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.thunks';

interface StateProps extends IBlotterPreferenceState {
  columns: readonly IBlotterColumnDefinition<SecondaryMarketProduct>[] | null;
  items: readonly SecondaryMarketProduct[] | null;
  isCpOrderEntitled: boolean;
  isLiveQuoteEntitled: boolean;
  isSearchingProducts: boolean;
}

type DispatchProps = {
  onSelectProduct: (row: { isin: string | undefined }) => void;
  onBlotterColumnStateChanged: (
    columnState: readonly ColumnPreferenceState[],
    newColumnGroups?: readonly ColumnGroupPreferenceState[],
  ) => void;
  onValuesChangedRequest: (change: ProductChange) => void;
  onRemoveProduct: (item: { rowId: string; fileCode: string; isin: string }) => void;
  onProductFocused: (id?: string) => void;
  onRemoveAll: () => void;
  onRefreshProduct: (productIdentifier: { bcsKey: string; dlpKey?: string }) => void;
  onRefreshAll: () => void;
  onOpenCPOrderTab: (rowId: string, tradeWay: TradeWay) => void;
  onOpenLiveTab: (rowId: string, tradeWay: TradeWay) => void;
  onOpenLimitOrderTab: (rowId: string) => void;
  onFetchProductUnderlyings: (rowId: string) => void;
  onOpenDlpProduct: (isin: string) => void;
};

export type ResultGridsProps = StateProps & DispatchProps;

const mapStateToProps = (state: AppState): StateProps => {
  return {
    ...fromSecondaryMarket.productSearch.getSecondaryMarketProductsPreferenceState(state),
    columns: fromSecondaryMarket.productSearch.getSecondaryMarketProductSearchColumns(state),
    items: fromSecondaryMarket.productSearch.getSecondaryMarketProductSearchItems(state),
    isCpOrderEntitled: isSecondaryMarketCpOrderEnabledForUser(state),
    isLiveQuoteEntitled: isSecondaryMarketLiveQuoteEnabledForUser(state),
    isSearchingProducts: getIsSearchingProducts(state),
  };
};

export const mapDispatchToProps = (dispatch: ThunkDispatch): DispatchProps => ({
  onSelectProduct: ({ isin }) => {
    if (isin !== undefined) {
      dispatch(secondaryMarketAstroProductFetchRequestedThunk(isin));
    }
  },
  onBlotterColumnStateChanged(
    columnState: readonly ColumnPreferenceState[],
    columnGroupState?: readonly ColumnGroupPreferenceState[],
  ) {
    dispatch(secondaryMarketProductsColumnStateChanged(columnState, columnGroupState));
  },
  onRemoveProduct: ({ rowId }) => {
    logAnalytics.trackEvent('Secondary market', 'Delete product');
    dispatch(secondaryMarketProductsRemoveItem(rowId));
  },
  onRemoveAll: () => {
    logAnalytics.trackEvent('Secondary market', 'Delete all products');
    dispatch(secondaryMarketProductsClearAll());
  },
  onRefreshProduct: ({ bcsKey, dlpKey }) => {
    logAnalytics.trackEvent('Secondary market', 'Refresh product');
    dispatch(secondaryMarketProductRefreshRequested([bcsKey], dlpKey !== undefined ? [dlpKey] : []));
  },
  onRefreshAll: () => {
    logAnalytics.trackEvent('Secondary market', 'Refresh all products');
    dispatch(secondaryMarketProductRefreshAllRequested());
  },
  onValuesChangedRequest: (change: ProductChange) => {
    dispatch(secondaryMarketProductsValueChangedThunk(change));
  },
  onOpenCPOrderTab: (rowId: string, tradeWay: TradeWay) => {
    logAnalytics.trackEvent('Secondary market', 'Open CP Order form');
    dispatch(secondaryCPOrderTabCreatedFromProductSearchThunk(rowId, tradeWay));
  },
  onOpenLiveTab: (rowId: string, tradeWay: TradeWay) => {
    logAnalytics.trackEvent('Secondary market', 'Open Real Time screen');
    dispatch(liveThunks.tabCreatedThunk(rowId, tradeWay));
  },
  onFetchProductUnderlyings: rowId => {
    dispatch(searchProductUnderlyingsThunk(rowId));
  },
  onOpenLimitOrderTab: rowId => {
    dispatch(limitOrderThunks.tabCreatedThunk(rowId));
  },
  onOpenDlpProduct: isin => {
    dispatch(secondaryMarketQuoteByIsinReloadRequested(isin, 'dlpProducts'));
  },
  onProductFocused: id => {
    dispatch(secondaryMarketProductSearchFocused(id));
  },
});

export const connectResultsGrid = connect(mapStateToProps, mapDispatchToProps);
